import {
	CheckedState,
	MultiCheckbox,
	MultiCheckboxItemProps,
} from "@/components/molecules/multiCheckbox";
import { envVariable, Variable } from "@/common/constants/envVariable";
import { useAccountStyle } from "./style";
import { useCallback, useMemo } from "react";
import { useIntl } from "react-intl";
import { Queue } from "@/repository/models";
import {
	agentTotalWorkloadQueryKey,
	getAgentTotalWorkload,
	setQueueJoinStatus,
} from "@/repository/queryFns";
import {
	Button,
	Divider,
	Text,
	mergeClasses,
} from "@fluentui/react-components";
import {
	ArrowSync20Regular,
	ChevronRight16Regular,
} from "@fluentui/react-icons";
import { Flex, GapSize } from "@/components/atoms/flex";
import {
	QueuesActions,
	QueuesState,
	useQueuesStore,
} from "@/common/stores/nonpersistingStores/queues";
import { useMutation, useQuery } from "@tanstack/react-query";
import { CurrentUserPresence } from "../currentUserPresence";
import { useAppSettingsStore } from "@/common/stores/persistingStores/app/appSettingsStore";
import { AppSettingsState } from "@/common/stores/persistingStores/app";
import { WorkloadMenu } from "@/components/molecules/workloadMenu";
import { Lockr } from "@/common/Lockr";
import { StorageKeys } from "@/common/constants/storageKeys";
import { WorkloadMenuType } from "@/components/molecules/workloadMenu/types";
import { RefreshAgentButton } from "@/components/atoms/refreshAgent";
import { captureError } from "@/common/helpers/sentryHelper";
import FeedbackButton from "@/components/atoms/feedback/FeedbackButton";
import { SignOutButton } from "@/components/atoms/signOutButton";
import { DebugInfoActions, DebugInfoKey, WindowReloadReason, useDebugStore } from "@/common/stores/persistingStores/debugStore";

export const AccountDetails = () => {
	const intl = useIntl();
	const classes = useAccountStyle();
	const queues = useQueuesStore((state: QueuesState) => state.queues);
	const queueActions = useQueuesStore((actions: QueuesActions) => actions);
	const isNewClientEnabled = useAppSettingsStore(
		(state: AppSettingsState) => state.settings.isNewClientEnabled
	);
	const debugInfoActions = useDebugStore((state: DebugInfoActions) => state);

	const isJoinedOnAnyQueue = useQueuesStore(
		(state: QueuesState) => state.isJoinedOnAnyQueue
	);

	const queueJoinedMutation = useMutation({
		mutationFn: (data: Queue) =>
			setQueueJoinStatus(data.id, data.type, data.isAttached),
		onMutate: (queue: Queue): Queue => {
			queueActions.setPending(queue.id);
			queueActions.update(queue);

			return { ...queue, isAttached: !queue.isAttached };
		},
		onError: (err: any, data: Queue, context?: Queue) => {
			captureError(err, "Error joining queue", undefined, data);
			if (!!context) {
				queueActions.update(context);
			}
			queueActions.clearPending(data.id);
		},
		onSuccess: (_result: any, data: Queue) => {
			queueActions.clearPending(data.id);
		},
	});

	const mappedQueues = useMemo<MultiCheckboxItemProps[]>(
		() =>
			!!queues
				? queues.map((x: Queue) => ({
						key: x.id,
						label: x.name,
						checked: x.isAttached,
						disabled: !x.canJoin,
					}))
				: [],
		[queues]
	);

	const onCheckBoxChange = useCallback(
		(item: MultiCheckboxItemProps, checked: CheckedState) => {
			const queue = queues.find((x: Queue) => x.id === item.key) as Queue;
			queueJoinedMutation.mutate({ ...queue, isAttached: !!checked });
		},
		[queues, queueActions]
	);

	const switchClient = useCallback(() => {
		debugInfoActions.pushDebugInfoItem(DebugInfoKey.WindowReloadTriggerEvents, WindowReloadReason.SwitchClient);
		document.cookie =
			"preview=never; max-age=60480000; SameSite=None; Secure; Partitioned;";
		
		setTimeout(() => {
			window.location.reload();
		}, 500);
	}, []);

	const { data: agentTotalWorkload } = useQuery<number>({
		queryKey: [agentTotalWorkloadQueryKey],
		queryFn: getAgentTotalWorkload,
	});

	const totalWorkloadItem = useCallback(
		() => (
			<Flex
				hAlign="space-between"
				className={classes.workloadMenuTrigger}
				vAlign="center"
			>
				<Flex gap={GapSize.Medium} vAlign="center">
					<Text>{intl.formatMessage({ id: "Account.Workload" })}</Text>
					<Text>{agentTotalWorkload}%</Text>
				</Flex>
				{!!agentTotalWorkload && (
					<Flex>
						<ChevronRight16Regular />
					</Flex>
				)}
			</Flex>
		),
		[agentTotalWorkload]
	);

	const workloadMenu = useCallback(
		() => (
			<WorkloadMenu
				agentId={Lockr.get(StorageKeys.UserId)}
				menuType={WorkloadMenuType.CurrentAgent}
				trigger={totalWorkloadItem()}
				totalWorkload={agentTotalWorkload ?? 0}
			/>
		),
		[agentTotalWorkload]
	);

	const version = (
		<Flex gap={GapSize.Medium} vAlign="center" hAlign="center">
			<Text className={classes.versionText}>
				{intl.formatMessage({ id: "Account.Version" })}:{" "}
				{envVariable(Variable.Version)}
			</Text>
		</Flex>
	);

	return (
		<Flex column gap={GapSize.Medium} className={classes.container}>
			<CurrentUserPresence />

			<Divider />

			{workloadMenu()}
			<Divider />

			<Text
				className={mergeClasses(
					classes.queuesLabel,
					!isJoinedOnAnyQueue ? classes.isNotJoinedColor : ""
				)}
			>
				{intl.formatMessage({ id: "Account.ActiveQueues" })}
			</Text>

			<MultiCheckbox
				items={mappedQueues}
				onChange={onCheckBoxChange}
				classNames={{
					root: classes.multiCheckbox,
					item: classes.multiCheckboxItem,
				}}
			/>
			{/* <Divider />

			<Button
				appearance="transparent"
				className={classes.button}
				icon={<Person20Regular />}
				children={intl.formatMessage({ id: "Account.ManageProfile" })}
			/>
			<Button
				appearance="transparent"
				className={classes.button}
				icon={<Settings20Regular />}
				children={intl.formatMessage({ id: "Account.PageSettings" })}
			/> */}

			<Divider />
			{!isNewClientEnabled && (
				<Button
					appearance="transparent"
					className={classes.button}
					icon={<ArrowSync20Regular />}
					children={intl.formatMessage({ id: "Account.SwitchClient" })}
					onClick={switchClient}
				/>
			)}
			<RefreshAgentButton />
			<FeedbackButton />
			<SignOutButton className={classes.button} />
			<Divider />
			{version}
		</Flex>
	);
};
